import { Swiper } from 'swiper';
import { Navigation, Controller } from 'swiper/modules';

export interface SwiperConfig {
    onRealIndexChange?: () => void;
    allowTouchMove?: boolean;
    control?: Swiper | null;
    autoHeight?: boolean;
    speed?: number;
}

/**
 * Service for Swiper slider initialization and management
 */
export class SwiperService {
    /**
     * Initialize Swiper instance
     * @param container - Container element for Swiper
     * @param config - Configuration object with callbacks
     * @returns Swiper instance
     */
    static initialize(container: HTMLElement, config?: SwiperConfig): Swiper {
        return new Swiper(container, {
            modules: [Navigation, Controller],
            loop: true,
            slidesPerView: 1,
            spaceBetween: 0,
            speed: config?.speed ?? 300,
            allowTouchMove: config?.allowTouchMove ?? true,
            autoHeight: config?.autoHeight ?? false,
            on: {
                realIndexChange: () => {
                    config?.onRealIndexChange?.();
                },
            },
        });
    }

    /**
     * Link two swipers to control each other
     * @param a - First swiper instance
     * @param b - Second swiper instance
     */
    static linkControllers(a: Swiper | null, b: Swiper | null): void {
        if (a && b && a.controller && b.controller) {
            a.controller.control = b;
            b.controller.control = a;
        }
    }

    /**
     * Get current slide index (real index, accounting for loop)
     * @param swiper - Swiper instance
     * @returns Current real index
     */
    static getCurrentIndex(swiper: Swiper | null): number {
        return swiper?.realIndex ?? 0;
    }

    /**
     * Navigate to previous slide
     * @param swiper - Swiper instance
     */
    static slidePrev(swiper: Swiper | null): void {
        swiper?.slidePrev();
    }

    /**
     * Navigate to next slide
     * @param swiper - Swiper instance
     */
    static slideNext(swiper: Swiper | null): void {
        swiper?.slideNext();
    }
}
